import React from "react";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BarChartIcon from "@mui/icons-material/BarChart";
import GroupsIcon from "@mui/icons-material/Groups";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import AddCardIcon from "@mui/icons-material/AddCard";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import Divider from "@mui/material/Divider";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  ListItem,
  ListItemButton,
  Typography,
  List,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { Pages } from "../../utils/pages";
import logo from "../../images/open.svg";
import { setAccessToken, setRefreshAccessToken } from "../../utils/tokenStore";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { endsWithNumber } from "../../utils/math";
import { useQuery } from "react-query";
import { fetchCurrentUser } from "../../api/user";

const drawerWidth = 200;

const SideBar: React.FC<{}> = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [beneNavMenuOpen, setBeneNavMenuOpen] = React.useState(true);
  const [gestioneNavMenuOpen, setGestioneNavMenuOpen] = React.useState(false);

  // Fetch current user data
  const { data: userData } = useQuery({
    queryKey: ["currentUser"],
    queryFn: fetchCurrentUser,
    staleTime: Infinity,
  });

  const userName = userData?.data?.name || "";

  // Keep Gestione menu open if user is on a child page
  React.useEffect(() => {
    const gestionePages = [
      Pages.BENE_COLLEZIONE,
      Pages.BENE_BRAND,
      Pages.BENE_MATERIALE,
      Pages.BENE_QUALITA,
      Pages.BENE_TIPOLOGIA,
      Pages.BENE_COLORE,
      Pages.BENE_UBICAZIONE
    ];
    
    if (gestionePages.some(page => pathname.includes(page))) {
      setGestioneNavMenuOpen(true);
    }
  }, [pathname]);

  const handleBeneClick = () => {
    setBeneNavMenuOpen(!beneNavMenuOpen);
  };

  const handleGestioneClick = () => {
    setGestioneNavMenuOpen(!gestioneNavMenuOpen);
  };

  return (
    <>
      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: "background.default", p: 3 }}
      >
        <div className="home">
          <Drawer
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
              },
            }}
            variant="permanent"
            anchor="left"
          >
            <Box
              component="img"
              display={"block"}
              sx={{
                margin: "10pt",
              }}
              alt="OpenCloset"
              src={logo}
            />
            <Box sx={{ textAlign: "center", mb: 2, px: 2 }}>
              {userName && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: "bold", 
                    color: "#4caf50"
                  }}
                >
                  {userName}
                </Typography>
              )}
            </Box>
            <List>
              <ListItem
                key={140}
                disablePadding
                onClick={() => navigate(Pages.HOME)}
              >
                <ListItemButton selected={Pages.HOME === pathname}>
                  <ListItemIcon>
                    <DashboardIcon className="icon" />
                  </ListItemIcon>
                  <ListItemText primary={"Dashboard"} />
                </ListItemButton>
              </ListItem>
              <ListItem
                key={141}
                disablePadding
                onClick={() => navigate(Pages.STATISTICHE)}
              >
                <ListItemButton selected={Pages.STATISTICHE === pathname}>
                  <ListItemIcon>
                    <BarChartIcon className="icon" />
                  </ListItemIcon>
                  <ListItemText primary={"Statistiche"} />
                </ListItemButton>
              </ListItem>
              <ListItem
                key={14}
                disablePadding
                onClick={() => navigate(Pages.PERSONA_FISICA)}
              >
                <ListItemButton
                  selected={pathname.includes(Pages.PERSONA_FISICA)}
                >
                  <ListItemIcon>
                    <GroupsIcon className="icon" />
                  </ListItemIcon>
                  <ListItemText primary={"Cliente"} />
                </ListItemButton>
              </ListItem>
              <ListItem
                key={15}
                disablePadding
                onClick={() => navigate(Pages.CONTRATTO)}
              >
                <ListItemButton selected={pathname.includes(Pages.CONTRATTO)}>
                  <ListItemIcon>
                    <AddCardIcon className="icon" />
                  </ListItemIcon>
                  <ListItemText primary={"Contratti"} />
                </ListItemButton>
              </ListItem>
              <ListItem
                key={16}
                disablePadding
                onClick={() => navigate(Pages.BENE)}
              >
                <ListItemButton
                  selected={
                    pathname.endsWith(Pages.BENE) ||
                    (pathname.includes(Pages.BENE) && endsWithNumber(pathname))
                  }
                >
                  <ListItemIcon>
                    <ShoppingBasketIcon className="icon" />
                  </ListItemIcon>
                  <ListItemText primary={"Beni"} />
                </ListItemButton>
              </ListItem>
              <ListItemButton
                key={21}
                onClick={handleGestioneClick}
              >
                <ListItemIcon>
                  <TuneIcon className="icon" />
                </ListItemIcon>
                <ListItemText primary={"Gestione"} />
                {gestioneNavMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={gestioneNavMenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    onClick={() => navigate(Pages.BENE_COLLEZIONE)}
                    sx={{ pl: 4 }}
                    selected={pathname.includes(Pages.BENE_COLLEZIONE)}
                  >
                    <ListItemText primary={"Collezioni"} />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => navigate(Pages.BENE_BRAND)}
                    sx={{ pl: 4 }}
                    selected={pathname.includes(Pages.BENE_BRAND)}
                  >
                    <ListItemText primary={"Brand"} />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => navigate(Pages.BENE_MATERIALE)}
                    sx={{ pl: 4 }}
                    selected={pathname.includes(Pages.BENE_MATERIALE)}
                  >
                    <ListItemText primary={"Materiali"} />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => navigate(Pages.BENE_QUALITA)}
                    sx={{ pl: 4 }}
                    selected={pathname.includes(Pages.BENE_QUALITA)}
                  >
                    <ListItemText primary={"Condizioni"} />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => navigate(Pages.BENE_TIPOLOGIA)}
                    sx={{ pl: 4 }}
                    selected={pathname.includes(Pages.BENE_TIPOLOGIA)}
                  >
                    <ListItemText primary={"Tipologie"} />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => navigate(Pages.BENE_COLORE)}
                    sx={{ pl: 4 }}
                    selected={pathname.includes(Pages.BENE_COLORE)}
                  >
                    <ListItemText primary={"Colori"} />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => navigate(Pages.BENE_UBICAZIONE)}
                    sx={{ pl: 4 }}
                    selected={pathname.includes(Pages.BENE_UBICAZIONE)}
                  >
                    <ListItemText primary={"Ubicazioni"} />
                  </ListItemButton>
                </List>
              </Collapse>

              {/* <ListItem key={16} disablePadding onClick={() => navigate(Pages.BENE)}>
                <ListItemButton selected={pathname.includes(Pages.BENE)}>
                  <ListItemIcon>
                    <ShoppingBasketIcon className="icon" />
                  </ListItemIcon>
                  <ListItemText primary={"Beni"} />
                </ListItemButton>
              </ListItem> */}
              <ListItem
                key={4}
                disablePadding
                onClick={() => {
                  navigate(Pages.CONFIGURATION);
                }}
              >
                <ListItemButton
                  selected={pathname.includes(Pages.CONFIGURATION)}
                >
                  <ListItemIcon>
                    <SettingsIcon className="icon" />
                  </ListItemIcon>
                  <ListItemText primary={"Configurazione"} />
                </ListItemButton>
              </ListItem>
              <ListItem
                key={66}
                disablePadding
                onClick={() => {
                  setAccessToken("");
                  setRefreshAccessToken("");
                  navigate(Pages.ROOT);
                }}
              >
                <ListItemButton>
                  <ListItemIcon>
                    <ExitToAppIcon className="icon" />
                  </ListItemIcon>
                  <ListItemText primary={"Disconnetti"} />
                </ListItemButton>
              </ListItem>
            </List>
          </Drawer>
        </div>
      </Box>
    </>
  );
};

export default SideBar;
